import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Plus } from "lucide-react";
import OnboardingShell from "@/components/jk/OnboardingShell";
import { PrimaryButton, TextField } from "@/components/jk/ui";
import { Switch, Chip, SectionLabel } from "@/components/jk/kit";
import { useAuth } from "@/context/AuthContext";
import { useMenu } from "@/context/MenuContext";
import api from "@/lib/api";

const PRESETS = ["Jathagam Reading", "Marriage Compatibility", "Career Guidance", "Health Reading", "General Query", "Prasnam"];
const DURATIONS = [15, 30, 45, 60];
const MODES = ["Phone Call", "Video Call", "In Person"];

const TIERS = [
  { key: "tier1", n: 1 },
  { key: "tier2", n: 2 },
  { key: "tier3", n: 3 },
];

const blank = (name, custom = false) => ({
  name, enabled: false,
  price_inr: "", duration_minutes: 45,
  tier1_price_inr: "", tier1_duration_minutes: 45, tier1_consultation_modes: ["Phone Call"],
  tier2_price_inr: "", tier2_duration_minutes: 45, tier2_consultation_modes: ["In Person"],
  tier3_price_inr: "", tier3_duration_minutes: 45, tier3_consultation_modes: ["Phone Call", "In Person"],
  is_custom: custom,
});

const OnboardingServices = () => {
  const navigate = useNavigate();
  const { astrologer, onboardingComplete } = useAuth();
  const { open: openMenu } = useMenu();
  const editMode = !!onboardingComplete;
  const backToMenu = () => { openMenu(); navigate(-1); };
  const [services, setServices] = useState(PRESETS.map((p) => blank(p)));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/astrologer/services").then(({ data }) => {
      if (data.services?.length) {
        const map = {};
        data.services.forEach((s) => (map[s.name] = s));
        setServices((prev) => {
          const merged = prev.map((p) => map[p.name]
            ? {
                ...p, enabled: true,
                price_inr: String(map[p.name].price_inr),
                duration_minutes: map[p.name].duration_minutes,
                tier1_price_inr: String(map[p.name].tier1_price_inr ?? map[p.name].price_inr ?? ""),
                tier1_duration_minutes: map[p.name].tier1_duration_minutes ?? map[p.name].duration_minutes ?? 45,
                tier1_consultation_modes: map[p.name].tier1_consultation_modes ?? ["Phone Call"],
                tier2_price_inr: String(map[p.name].tier2_price_inr ?? ""),
                tier2_duration_minutes: map[p.name].tier2_duration_minutes ?? 45,
                tier2_consultation_modes: map[p.name].tier2_consultation_modes ?? ["In Person"],
                tier3_price_inr: String(map[p.name].tier3_price_inr ?? ""),
                tier3_duration_minutes: map[p.name].tier3_duration_minutes ?? 45,
                tier3_consultation_modes: map[p.name].tier3_consultation_modes ?? ["Phone Call", "In Person"],
              }
            : p);
          const extra = data.services.filter((s) => !PRESETS.includes(s.name))
            .map((s) => ({
              name: s.name, enabled: true,
              price_inr: String(s.price_inr), duration_minutes: s.duration_minutes,
              tier1_price_inr: String(s.tier1_price_inr ?? s.price_inr ?? ""),
              tier1_duration_minutes: s.tier1_duration_minutes ?? s.duration_minutes ?? 45,
              tier1_consultation_modes: s.tier1_consultation_modes ?? ["Phone Call"],
              tier2_price_inr: String(s.tier2_price_inr ?? ""),
              tier2_duration_minutes: s.tier2_duration_minutes ?? 45,
              tier2_consultation_modes: s.tier2_consultation_modes ?? ["In Person"],
              tier3_price_inr: String(s.tier3_price_inr ?? ""),
              tier3_duration_minutes: s.tier3_duration_minutes ?? 45,
              tier3_consultation_modes: s.tier3_consultation_modes ?? ["Phone Call", "In Person"],
              is_custom: true,
            }));
          return [...merged, ...extra];
        });
      }
    }).catch(() => {});
  }, []);

  const update = (i, patch) => setServices((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const toggleTierMode = (i, tierKey, mode) => {
    const cur = new Set(services[i][`${tierKey}_consultation_modes`] || []);
    cur.has(mode) ? cur.delete(mode) : cur.add(mode);
    update(i, { [`${tierKey}_consultation_modes`]: [...cur] });
  };

  const addCustom = () => setServices((s) => [...s, { ...blank("", true), enabled: true }]);

  const handleContinue = async () => {
    const active = services.filter((s) => s.enabled && s.name.trim());
    if (active.length === 0) { setError("Enable at least one service to continue."); return; }
    if (active.some((s) => !s.tier1_price_inr || Number(s.tier1_price_inr) <= 0)) {
      setError("Set a Tier 1 fee for each enabled service."); return;
    }
    setSaving(true);
    try {
      await api.post("/astrologer/services", {
        services: active.map((s) => ({
          name: s.name.trim(),
          price_inr: Number(s.tier1_price_inr) || Number(s.price_inr) || 0,
          duration_minutes: s.tier1_duration_minutes || s.duration_minutes || 45,
          tier1_price_inr: Number(s.tier1_price_inr) || null,
          tier1_duration_minutes: s.tier1_duration_minutes || null,
          tier1_consultation_modes: s.tier1_consultation_modes || [],
          tier2_price_inr: Number(s.tier2_price_inr) || null,
          tier2_duration_minutes: s.tier2_duration_minutes || null,
          tier2_consultation_modes: s.tier2_consultation_modes || [],
          tier3_price_inr: Number(s.tier3_price_inr) || null,
          tier3_duration_minutes: s.tier3_duration_minutes || null,
          tier3_consultation_modes: s.tier3_consultation_modes || [],
          consultation_modes: [...new Set([
            ...(s.tier1_consultation_modes || []),
            ...(s.tier2_consultation_modes || []),
            ...(s.tier3_consultation_modes || []),
          ])],
          is_custom: !!s.is_custom,
        })),
      });
      if (editMode) { toast.success("Services updated"); backToMenu(); }
      else navigate("/onboarding/availability");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save services.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <OnboardingShell
      step={editMode ? null : 2}
      title={editMode ? "Edit Services" : "Your Services"}
      subtitle="Turn on the consultations you offer and set your fees."
      onBack={() => editMode ? backToMenu() : navigate("/onboarding/profile")}
      testid="onboarding-services-screen"
      footer={
        <>
          {error && <p className="mb-3 text-center text-[13px] text-[#d23b2f]" data-testid="services-error">{error}</p>}
          <PrimaryButton testid="services-continue" onClick={handleContinue} disabled={saving} icon={<ArrowRight size={18} strokeWidth={2} />}>
            {saving ? "Saving…" : editMode ? "Save changes" : "Continue"}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-3">
        {services.map((s, i) => (
          <div key={i} className={`overflow-hidden rounded-[16px] border transition-colors ${s.enabled ? "border-[#111111] bg-white" : "border-[#e9e6e1] bg-white"}`} data-testid={`service-card-${i}`}>
            <div className="flex items-center justify-between gap-3 p-4">
              {s.is_custom && s.enabled ? (
                <input
                  data-testid={`service-name-${i}`}
                  value={s.name}
                  placeholder="Custom service name"
                  onChange={(e) => update(i, { name: e.target.value })}
                  className="flex-1 bg-transparent text-[15px] font-semibold text-[#111111] outline-none placeholder:text-[#aaaaaa]"
                />
              ) : (
                <span className="text-[15px] font-semibold text-[#111111]">{s.name}</span>
              )}
              <Switch testid={`service-toggle-${i}`} on={s.enabled} onChange={(v) => update(i, { enabled: v })} />
            </div>

            {s.enabled && (
              <div className="space-y-3 border-t border-[#f0ede8] px-4 pb-5 pt-4">
                {TIERS.map(({ key, n }) => (
                  <div key={key} className="rounded-[12px] bg-[#f9f8f5] p-3 space-y-3">
                    <TextField
                      testid={`service-${i}-${key}-price`}
                      label={`Tier ${n} Price (₹)`}
                      type="number"
                      inputMode="numeric"
                      prefix="₹"
                      placeholder="0"
                      value={s[`${key}_price_inr`]}
                      onChange={(e) => { update(i, { [`${key}_price_inr`]: e.target.value }); setError(""); }}
                    />
                    <div>
                      <SectionLabel className="mb-2">DURATION (IN MINS)</SectionLabel>
                      <div className="flex gap-2">
                        {DURATIONS.map((d) => (
                          <Chip
                            key={d}
                            selected={s[`${key}_duration_minutes`] === d}
                            onClick={() => update(i, { [`${key}_duration_minutes`]: d })}
                            testid={`service-${i}-${key}-dur-${d}`}
                          >{d}</Chip>
                        ))}
                      </div>
                    </div>
                    <div>
                      <SectionLabel className="mb-2">MODE</SectionLabel>
                      <div className="flex flex-wrap gap-2">
                        {MODES.map((m) => (
                          <Chip
                            key={m}
                            selected={(s[`${key}_consultation_modes`] || []).includes(m)}
                            onClick={() => toggleTierMode(i, key, m)}
                            testid={`service-${i}-${key}-mode-${m.replace(/\s+/g, "-").toLowerCase()}`}
                          >{m}</Chip>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <button onClick={addCustom} data-testid="add-custom-service" className="flex w-full items-center justify-center gap-2 rounded-[16px] border border-dashed border-[#cfccc6] bg-white py-4 text-[14px] font-medium text-[#666666] transition-colors hover:border-[#111111] hover:text-[#111111]">
          <Plus size={18} strokeWidth={2} /> Add custom service
        </button>
      </div>
    </OnboardingShell>
  );
};

export default OnboardingServices;
